import React from 'react';
import { motion } from 'framer-motion';
import { Check, Minus, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { COMPARISON_COLUMNS, COMPARISON_ROWS } from './offerData';
import type { ComparisonCell, ComparisonLevel } from './types';

const LEVEL_ICON: Record<ComparisonLevel, { Icon: LucideIcon; className: string }> = {
  good: { Icon: Check, className: 'text-emerald-400' },
  neutral: { Icon: Minus, className: 'text-gray-400' },
  bad: { Icon: X, className: 'text-rose-400' },
};

interface CellContentProps {
  cell: ComparisonCell;
  highlight?: boolean;
}

const CellContent: React.FC<CellContentProps> = ({ cell, highlight = false }) => {
  const { Icon, className } = LEVEL_ICON[cell.level];
  return (
    <span className={`inline-flex items-start gap-2 text-sm ${highlight ? 'text-white font-semibold' : 'text-gray-300'}`}>
      <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${className}`} aria-hidden="true" /> {cell.text}
    </span>
  );
};

const COMPARISON_SHORT_LABELS = { agency: 'Agence', diy: 'Wix, etc.' } as const;

const OfferComparison: React.FC = () => (
  <section className="py-16 bg-[#36393F]">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        className="text-center mb-10"
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.45, ease: 'easeOut' }}
      >
        <h2 className="text-balance text-2xl md:text-3xl font-bold text-white mb-3">
          Pourquoi choisir <span className="text-[#5865F2]">SOS IT Services</span> ?
        </h2>
        <p className="text-gray-300 max-w-2xl mx-auto">Comparez avec les deux alternatives habituelles.</p>
      </motion.div>

      {/* Mobile : une ligne compacte par critère */}
      <ul className="md:hidden divide-y divide-white/10 rounded-xl border border-white/10 bg-white/5">
        {COMPARISON_ROWS.map((row) => (
          <li key={row.criterion} className="px-3 py-2.5">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-xs uppercase tracking-wider text-[#AEB9FF]">{row.criterion}</span>
              <span className="text-sm">
                <CellContent cell={row.sos} highlight />
              </span>
            </div>
            <p className="mt-1 text-xs text-gray-400">
              {COMPARISON_SHORT_LABELS.agency} : {row.agency.text} · {COMPARISON_SHORT_LABELS.diy} : {row.diy.text}
            </p>
          </li>
        ))}
      </ul>

      {/* Desktop : tableau */}
      <div className="hidden md:block overflow-hidden rounded-2xl border border-white/10">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-white/5">
              <th scope="col" className="p-4"><span className="sr-only">Critère</span></th>
              <th scope="col" className="p-4 text-sm font-semibold text-gray-300">{COMPARISON_COLUMNS.agency}</th>
              <th scope="col" className="p-4 text-sm font-semibold text-gray-300">{COMPARISON_COLUMNS.diy}</th>
              <th scope="col" className="p-4 text-sm font-bold text-white bg-[#5865F2]/30 border-x border-[#5865F2]/50">
                {COMPARISON_COLUMNS.sos}
              </th>
            </tr>
          </thead>
          <tbody>
            {COMPARISON_ROWS.map((row) => (
              <tr key={row.criterion} className="border-t border-white/10">
                <th scope="row" className="p-4 text-sm font-medium text-white">{row.criterion}</th>
                <td className="p-4"><CellContent cell={row.agency} /></td>
                <td className="p-4"><CellContent cell={row.diy} /></td>
                <td className="p-4 bg-[#5865F2]/15 border-x border-[#5865F2]/50"><CellContent cell={row.sos} highlight /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </section>
);

export default OfferComparison;
